import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2, Sparkles, CheckCircle2, Circle, FolderPlus, Package, GripVertical, ChevronRight, ChevronDown, Pencil, Check, Eye, EyeOff } from 'lucide-react';
import * as C from '../constants';
import api, { getToken } from '../api';
import AlertToast from './AlertToast';
import ConfirmModal from './ConfirmModal';
import SimpleImageCropModal from './SimpleImageCropModal';
import { sha256Hex } from '../utils/browserCompat'; // http 站点没有 crypto.subtle，去重要能降级

/**
 * SkillboxConfigModal — 技能工具箱配置弹窗（超管），三层结构
 *
 * 工具集（toolset）→ 分组（group）→ 技能（preset）
 *
 * 左侧：三层树（工具集可建/删，分组挂在工具集下可建/删，技能挂在分组下可拖拽排序）
 *       组内排序最靠前的技能 = 该工具集在画布工具条上显示的「置顶技能」图标
 * 右侧：技能配置表单（LOGO / 名称 / 工具集下拉 / 分组下拉「随工具集联动」/ 提示词 / 描述词 / 启用）
 */

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4100 };
const modalStyle = { width: 1080, height: '84vh', background: 'var(--modal-surface)', border: '1px solid var(--border-strong)', borderRadius: 12, color: 'var(--text-body)', boxShadow: 'var(--shadow-panel)', display: 'flex', flexDirection: 'column' };
const header = { padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)', fontWeight: 700, fontSize: 14, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--modal-input-bg)', color: 'var(--text-primary)', outline: 'none', fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };
const btn = (primary) => ({ padding: '8px 16px', borderRadius: 8, border: '1px solid ' + (primary ? '#8b5cf6' : 'var(--border-strong)'), background: primary ? '#8b5cf6' : 'transparent', color: primary ? '#fff' : 'var(--text-body)', cursor: 'pointer', fontSize: 13 });

const emptyForm = { id: null, name: '', toolsetId: null, groupId: null, logoUrl: '', prompt: '', description: '', modelIds: [], enabled: true, referenceEnabled: false, referenceImages: [], referenceLabel: '风格' }; // 🆕 新增 referenceLabel

export default function SkillboxConfigModal({ open, onClose }) {
  const [tree, setTree] = useState([]);            // [{id,name,groups:[{id,name,presets:[]}]}]
  const [imageModels, setImageModels] = useState([]);   // 可选图像模型（供技能勾选白名单）
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoLoadError, setLogoLoadError] = useState(false);
  const [newToolsetName, setNewToolsetName] = useState('');
  const [showToolsetInput, setShowToolsetInput] = useState(false);   // 顶部「新增工具集」输入行开关
  const [renameToolsetId, setRenameToolsetId] = useState(null);      // 正在改名的工具集 id
  const [renameToolsetVal, setRenameToolsetVal] = useState('');      // 改名输入值
  const [groupInputFor, setGroupInputFor] = useState(null);  // 正在新增分组的 toolsetId
  const [newGroupName, setNewGroupName] = useState('');
  const [renameGroupId, setRenameGroupId] = useState(null);      // 正在改名的分组 id
  const [renameGroupVal, setRenameGroupVal] = useState('');      // 分组改名输入值
  const [collapsed, setCollapsed] = useState({});            // { [toolsetId]: true } 折叠态
  const [dragInfo, setDragInfo] = useState(null);            // 技能拖拽：{ presetId, groupId }
  const [dragToolsetInfo, setDragToolsetInfo] = useState(null);  // 工具集拖拽：{ toolsetId }
  const [dragGroupInfo, setDragGroupInfo] = useState(null);      // 分组拖拽：{ groupId, toolsetId }
  const [dragOverTarget, setDragOverTarget] = useState(null);    // 拖拽悬停目标：{ type: 'toolset'|'group'|'preset', id }
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [confirm, setConfirm] = useState(null);             // { kind, id, name }
  const [editToolsetLogo, setEditToolsetLogo] = useState(null);  // 正在编辑 Logo 的工具集 {id, name, logoUrl}
  const [editGroupLogo, setEditGroupLogo] = useState(null);      // 正在编辑 Logo 的分组 {id, name, logoUrl, toolsetId}
  const [uploadingEntityLogo, setUploadingEntityLogo] = useState(false);  // 工具集/分组 Logo 上传中
  const [entityLogoLoadError, setEntityLogoLoadError] = useState(false);  // 工具集/分组 Logo 加载失败
  const [refImageUploading, setRefImageUploading] = useState(false);      // 🎨 风格参考图上传中
  const [editingRefImage, setEditingRefImage] = useState(null);           // 🎨 正在编辑的参考图 {index, name, prompt}
  const [cropImageUrl, setCropImageUrl] = useState(null);                 // 🎨 裁剪图片URL

  const showToast = (msg, type = 'success', duration = 4000) => {
    setToastMessage(msg); setToastType(type);
    setTimeout(() => setToastMessage(''), duration);
  };
  const notifyUpdated = () => window.dispatchEvent(new Event('skillboxUpdated'));

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/admin/skillbox');
      setTree(data.toolsets || []);
    } catch (e) {
      showToast('❌ 加载技能失败：' + (e.message || '未知错误'), 'warning');
    } finally {
      setLoading(false);
    }
  };

  // 图像模型列表：与画布节点下拉同源（/api/models/by-type/image，已排除 toolbox 模型）
  const loadImageModels = async () => {
    try {
      const list = await api.get('/api/models/by-type/image');
      setImageModels(Array.isArray(list) ? list : []);
    } catch (e) {
      setImageModels([]);
      showToast('❌ 加载图像模型失败：' + (e.message || '未知错误'), 'warning');
    }
  };

  useEffect(() => { if (open) { loadData(); loadImageModels(); setForm(emptyForm); setLogoLoadError(false); setNewToolsetName(''); setShowToolsetInput(false); setRenameToolsetId(null); setGroupInputFor(null); setNewGroupName(''); setRenameGroupId(null); setEditToolsetLogo(null); setEditGroupLogo(null); } }, [open]);

  if (!open) return null;

  // 工具集 / 分组下拉数据
  const toolsetOptions = tree.map(t => ({ id: t.id, name: t.name }));
  const groupOptionsForToolset = (toolsetId) => {
    const t = tree.find(x => String(x.id) === String(toolsetId));
    return t ? (t.groups || []).map(g => ({ id: g.id, name: g.name })) : [];
  };

  const selectPreset = (p) => {
    console.log('🔍 selectPreset 收到的数据：', {
      id: p.id,
      name: p.name,
      referenceLabel: p.referenceLabel,
      '完整对象': p
    });
    setForm({
      id: p.id,
      name: p.name,
      toolsetId: p.toolsetId,
      groupId: p.groupId,
      logoUrl: p.logoUrl,
      prompt: p.prompt,
      description: p.description,
      modelIds: Array.isArray(p.modelIds) ? p.modelIds : [],
      enabled: p.enabled,
      referenceEnabled: p.referenceEnabled || false,
      referenceImages: Array.isArray(p.referenceImages) ? p.referenceImages : [],
      referenceLabel: p.referenceLabel || '风格'  // 🆕 从数据库读取自定义标签
    });
    setLogoLoadError(false);
  };
  const newPreset = (toolsetId = null, groupId = null) => { setForm({ ...emptyForm, toolsetId, groupId }); setLogoLoadError(false); };

  // ── 技能：保存 / 启用切换 / 删除 ──
  const handleSave = async () => {
    if (!form.name.trim()) { showToast('⚠️ 请填写技能名', 'warning'); return; }
    if (form.groupId == null) { showToast('⚠️ 请选择所属工具集和分组', 'warning'); return; }
    setSaving(true);
    const payload = {
      id: form.id || undefined,
      name: form.name.trim(),
      groupId: form.groupId,
      logoUrl: form.logoUrl || null,
      prompt: form.prompt,
      description: form.description,
      modelIds: form.modelIds || [],
      enabled: form.enabled,
      referenceEnabled: form.referenceEnabled || false,
      referenceImages: form.referenceImages || [],
      referenceLabel: form.referenceLabel || '风格', // 🆕 保存参考图标签
    };
    console.log('📤 保存技能工具箱，referenceLabel =', payload.referenceLabel);
    try {
      const result = await api.post('/api/admin/skillbox-presets', payload);
      showToast(form.id ? '✅ 已更新' : '✅ 已新增', 'success', 2500);
      await loadData();
      notifyUpdated();
      if (!form.id) setForm(emptyForm);
    } catch (e) {
      console.error('❌ 保存失败', e);
      showToast('❌ 保存失败：' + (e.message || '未知错误'), 'warning');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePreset = async (p) => {
    try {
      await api.post('/api/admin/skillbox-presets', {
        id: p.id,
        name: p.name,
        groupId: p.groupId,
        logoUrl: p.logoUrl,
        prompt: p.prompt,
        description: p.description,
        modelIds: p.modelIds || [],
        enabled: !p.enabled,
        referenceEnabled: p.referenceEnabled || false,
        referenceImages: p.referenceImages || [],
        referenceLabel: p.referenceLabel || '风格', // 🆕 保存参考图标签
      });
      if (form.id === p.id) setForm(f => ({ ...f, enabled: !f.enabled }));
      await loadData();
      notifyUpdated();
    } catch (e) {
      showToast('❌ 切换失败：' + (e.message || '未知错误'), 'warning');
    }
  };

  // ── 工具集：新增 / 改名 ──
  const handleAddToolset = async () => {
    const name = newToolsetName.trim();
    if (!name) return;
    try {
      await api.post('/api/admin/skillbox-toolsets', { name });
      setNewToolsetName('');
      setShowToolsetInput(false);
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ ' + (e.message || '新增工具集失败'), 'warning'); }
  };

  const handleRenameToolset = async (toolsetId) => {
    const name = renameToolsetVal.trim();
    if (!name) { setRenameToolsetId(null); return; }
    try {
      await api.post('/api/admin/skillbox-toolsets', { id: toolsetId, name });
      setRenameToolsetId(null);
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ ' + (e.message || '改名失败'), 'warning'); }
  };

  const handleAddGroup = async (toolsetId) => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      await api.post('/api/admin/skillbox-groups', { name, toolsetId });
      setNewGroupName(''); setGroupInputFor(null);
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ ' + (e.message || '新增分组失败'), 'warning'); }
  };

  const handleRenameGroup = async (groupId) => {
    const name = renameGroupVal.trim();
    if (!name) { setRenameGroupId(null); return; }
    try {
      await api.post('/api/admin/skillbox-groups', { id: groupId, name });
      setRenameGroupId(null);
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ ' + (e.message || '改名失败'), 'warning'); }
  };

  // 👁️ 切换分组名在前台工具箱的显示/隐藏——分组本身在这个配置面板里始终可见，
  // 只是控制前台下拉里是否展示这一行分组名文字（分割线和组内技能不受影响）
  const handleToggleGroupShowName = async (group) => {
    const nextShowName = !(group.showName !== false);
    try {
      await api.put(`/api/admin/skillbox-groups/${group.id}/show-name`, { showName: nextShowName });
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ ' + (e.message || '切换失败'), 'warning'); }
  };

  // ── 删除（工具集 / 分组 / 技能）──
  const handleDelete = async () => {
    if (!confirm) return;
    const { kind, id } = confirm;
    try {
      if (kind === 'toolset') await api.delete(`/api/admin/skillbox-toolsets/${id}`);
      else if (kind === 'group') await api.delete(`/api/admin/skillbox-groups/${id}`);
      else { await api.delete(`/api/admin/skillbox-presets/${id}`); if (form.id === id) setForm(emptyForm); }
      showToast('✅ 已删除', 'success', 2500);
      setConfirm(null);
      await loadData();
      notifyUpdated();
    } catch (e) {
      showToast('❌ 删除失败：' + (e.message || '未知错误'), 'warning');
      setConfirm(null);
    }
  };

  // ── 技能拖拽排序（组内）──
  const handleDropOnPreset = async (targetPreset) => {
    if (!dragInfo || dragInfo.presetId === targetPreset.id) { setDragInfo(null); setDragOverTarget(null); return; }
    if (dragInfo.groupId !== targetPreset.groupId) { setDragInfo(null); setDragOverTarget(null); return; } // 仅支持组内排序
    const group = tree.flatMap(t => t.groups).find(g => g.id === targetPreset.groupId);
    if (!group) { setDragInfo(null); setDragOverTarget(null); return; }
    const ids = group.presets.map(p => p.id);
    const from = ids.indexOf(dragInfo.presetId);
    const to = ids.indexOf(targetPreset.id);
    if (from < 0 || to < 0) { setDragInfo(null); setDragOverTarget(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    const order = ids.map((id, idx) => ({ id, sortOrder: idx }));
    setDragInfo(null);
    setDragOverTarget(null);
    try {
      await api.put('/api/admin/skillbox-presets/order', { order });
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ 排序保存失败', 'warning'); }
  };

  // ── 工具集拖拽排序 ──
  const handleDropOnToolset = async (targetToolset) => {
    if (!dragToolsetInfo || dragToolsetInfo.toolsetId === targetToolset.id) { setDragToolsetInfo(null); setDragOverTarget(null); return; }
    const ids = tree.map(t => t.id);
    const from = ids.indexOf(dragToolsetInfo.toolsetId);
    const to = ids.indexOf(targetToolset.id);
    if (from < 0 || to < 0) { setDragToolsetInfo(null); setDragOverTarget(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    const order = ids.map((id, idx) => ({ id, sortOrder: idx }));
    setDragToolsetInfo(null);
    setDragOverTarget(null);
    try {
      await api.put('/api/admin/skillbox-toolsets/order', { order });
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ 工具集排序保存失败', 'warning'); }
  };

  // ── 分组拖拽排序（同工具集内）──
  const handleDropOnGroup = async (targetGroup, targetToolsetId) => {
    if (!dragGroupInfo || dragGroupInfo.groupId === targetGroup.id) { setDragGroupInfo(null); setDragOverTarget(null); return; }
    // 仅支持同工具集内排序
    if (dragGroupInfo.toolsetId !== targetToolsetId) { setDragGroupInfo(null); setDragOverTarget(null); return; }
    const toolset = tree.find(t => t.id === targetToolsetId);
    if (!toolset) { setDragGroupInfo(null); setDragOverTarget(null); return; }
    const ids = toolset.groups.map(g => g.id);
    const from = ids.indexOf(dragGroupInfo.groupId);
    const to = ids.indexOf(targetGroup.id);
    if (from < 0 || to < 0) { setDragGroupInfo(null); setDragOverTarget(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    const order = ids.map((id, idx) => ({ id, sortOrder: idx, toolsetId: targetToolsetId }));
    setDragGroupInfo(null);
    setDragOverTarget(null);
    try {
      await api.put('/api/admin/skillbox-groups/order', { order });
      await loadData();
      notifyUpdated();
    } catch (e) { showToast('❌ 分组排序保存失败', 'warning'); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const buffer = await file.arrayBuffer();
      // 拿不到哈希（http 非安全上下文）就跳过去重预检，直接上传
      const hashHex = await sha256Hex(buffer);
      if (hashHex) {
        const checkResult = await api.get(`/api/upload/check-hash?hash=${hashHex}&type=logo`);
        if (checkResult.exists && checkResult.url) {
          setForm(f => ({ ...f, logoUrl: checkResult.url })); setLogoLoadError(false); setLogoUploading(false); e.target.value = ''; return;
        }
      }
      const formData = new FormData(); formData.append('file', file);
      const result = await fetch(`${C.API_BASE_URL}/api/upload?type=logo`, { method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }, body: formData }).then(r => r.json());
      if (result.url) { setForm(f => ({ ...f, logoUrl: result.url })); setLogoLoadError(false); }
      else if (result.error) { showToast('❌ 上传失败: ' + result.error, 'warning'); }
    } catch (err) {
      console.error('上传 Logo 失败:', err);
      showToast('❌ 上传失败', 'warning');
    }
    setLogoUploading(false);
    e.target.value = '';
  };

  // 工具集/分组 Logo 上传
  const handleEntityLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingEntityLogo(true);
    try {
      const buffer = await file.arrayBuffer();
      const hashHex = await sha256Hex(buffer);
      if (hashHex) {
        const checkResult = await api.get(`/api/upload/check-hash?hash=${hashHex}&type=logo`);
        if (checkResult.exists && checkResult.url) {
          if (editToolsetLogo) setEditToolsetLogo(e => ({ ...e, logoUrl: checkResult.url }));
          if (editGroupLogo) setEditGroupLogo(e => ({ ...e, logoUrl: checkResult.url }));
          setEntityLogoLoadError(false);
          setUploadingEntityLogo(false);
          e.target.value = '';
          return;
        }
      }
      const formData = new FormData();
      formData.append('file', file);
      const result = await fetch(`${C.API_BASE_URL}/api/upload?type=logo`, { method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }, body: formData }).then(r => r.json());
      if (result.url) {
        if (editToolsetLogo) setEditToolsetLogo(e => ({ ...e, logoUrl: result.url }));
        if (editGroupLogo) setEditGroupLogo(e => ({ ...e, logoUrl: result.url }));
        setEntityLogoLoadError(false);
      } else if (result.error) {
        showToast('❌ 上传失败: ' + result.error, 'warning');
      }
    } catch (err) {
      console.error('上传 Logo 失败:', err);
      showToast('❌ 上传失败', 'warning');
    }
    setUploadingEntityLogo(false);
    e.target.value = '';
  };

  // 保存工具集 Logo
  const handleSaveToolsetLogo = async () => {
    if (!editToolsetLogo) return;
    try {
      await api.post('/api/admin/skillbox-toolsets', { id: editToolsetLogo.id, name: editToolsetLogo.name, logoUrl: editToolsetLogo.logoUrl || null });
      showToast('✅ 工具集 Logo 已更新', 'success', 2500);
      setEditToolsetLogo(null);
      await loadData();
      notifyUpdated();
    } catch (e) {
      showToast('❌ 保存失败：' + (e.message || '未知错误'), 'warning');
    }
  };

  // 保存分组 Logo
  const handleSaveGroupLogo = async () => {
    if (!editGroupLogo) return;
    try {
      await api.post('/api/admin/skillbox-groups', { id: editGroupLogo.id, name: editGroupLogo.name, toolsetId: editGroupLogo.toolsetId, logoUrl: editGroupLogo.logoUrl || null });
      showToast('✅ 分组 Logo 已更新', 'success', 2500);
      setEditGroupLogo(null);
      await loadData();
      notifyUpdated();
    } catch (e) {
      showToast('❌ 保存失败：' + (e.message || '未知错误'), 'warning');
    }
  };

  // 🎨 风格参考图上传 - 先裁剪再上传
  const handleRefImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 读取图片并弹出裁剪框
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCropImageUrl(ev.target.result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // 裁剪完成后上传（SimpleImageCropModal 返回 blob 和 title）
  const handleCropComplete = async (croppedBlob, title) => {
    setCropImageUrl(null);
    setRefImageUploading(true);

    try {
      const file = new File([croppedBlob], 'reference.jpg', { type: 'image/jpeg' });

      const formData = new FormData();
      formData.append('file', file);
      const result = await fetch(`${C.API_BASE_URL}/api/admin/skillbox-reference-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData
      }).then(r => r.json());

      if (result.success && result.fullUrl && result.thumbnailUrl) {
        const newRefImage = {
          id: Date.now(),
          name: title || '风格参考',
          thumbnailUrl: result.thumbnailUrl,
          fullUrl: result.fullUrl,
          prompt: '以下图为参考，模仿其风格',
        };
        setForm(f => ({ ...f, referenceImages: [...(f.referenceImages || []), newRefImage] }));
        showToast('✅ 参考图上传成功', 'success');
      } else if (result.error) {
        showToast('❌ 上传失败: ' + result.error, 'warning');
      }
    } catch (err) {
      console.error('上传参考图失败:', err);
      showToast('❌ 上传失败', 'warning');
    }
    setRefImageUploading(false);
    setCropImageUrl(null);
  };

  // 🎨 删除参考图
  const handleDeleteRefImage = (imageId) => {
    setForm(f => ({
      ...f,
      referenceImages: (f.referenceImages || []).filter(img => img.id !== imageId)
    }));
  };

  // 🎨 更新参考图名称
  const handleUpdateRefImageName = (imageId, newName) => {
    setForm(f => ({
      ...f,
      referenceImages: (f.referenceImages || []).map(img =>
        img.id === imageId ? { ...img, name: newName } : img
      )
    }));
  };

  // 🎨 更新参考图提示词
  const handleUpdateRefImagePrompt = (imageId, newPrompt) => {
    setForm(f => ({
      ...f,
      referenceImages: (f.referenceImages || []).map(img =>
        img.id === imageId ? { ...img, prompt: newPrompt } : img
      )
    }));
  };

  const modalContent = (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={header}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Sparkles size={16} color="#8b5cf6" /> 技能工具箱</span>
          <X size={18} style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {/* 左：三层树 */}
          <div style={{ width: 380, borderRight: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: 12, borderBottom: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* 顶部大按钮：新增工具集 */}
              {!showToolsetInput ? (
                <button onClick={() => { setShowToolsetInput(true); setNewToolsetName(''); }} style={{ ...btn(true), width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  <Package size={14} /> 新增工具集
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input autoFocus style={{ ...inputStyle, padding: '7px 10px' }} value={newToolsetName}
                    onChange={e => setNewToolsetName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddToolset(); if (e.key === 'Escape') setShowToolsetInput(false); }}
                    placeholder="工具集名称" />
                  <button onClick={handleAddToolset} style={{ ...btn(true), padding: '7px 12px', flexShrink: 0 }}>添加</button>
                  <button onClick={() => setShowToolsetInput(false)} style={{ ...btn(false), padding: '7px 10px', flexShrink: 0 }}>取消</button>
                </div>
              )}
              <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.5 }}>层级：工具集 → 分组 → 技能。工具集后「<FolderPlus size={9} style={{ verticalAlign: 'middle' }} />」加分组，分组后「<Plus size={9} style={{ verticalAlign: 'middle' }} />」加技能。</div>
            </div>
            <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
              {loading ? (
                <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '20px 0' }}>加载中...</div>
              ) : tree.length === 0 ? (
                <div style={{ fontSize: 12, color: '#475569', textAlign: 'center', padding: '20px 0' }}>暂无工具集，点上方「新增工具集」</div>
              ) : tree.map(toolset => {
                const isCollapsed = collapsed[toolset.id];
                const isRenaming = renameToolsetId === toolset.id;
                return (
                  <div key={toolset.id} style={{ marginBottom: 8, position: 'relative' }}
                    draggable={!isRenaming}
                    onDragStart={(e) => {
                      if (!isRenaming) {
                        setDragToolsetInfo({ toolsetId: toolset.id });
                        e.dataTransfer.effectAllowed = 'move';
                      }
                    }}
                    onDragOver={(e) => {
                      if (dragToolsetInfo && dragToolsetInfo.toolsetId !== toolset.id) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        setDragOverTarget({ type: 'toolset', id: toolset.id });
                      }
                    }}
                    onDragLeave={() => setDragOverTarget(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleDropOnToolset(toolset);
                    }}>
                    {/* 工具集行 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '6px 4px',
                      borderRadius: 6,
                      background: dragToolsetInfo?.toolsetId === toolset.id
                        ? 'rgba(139,92,246,0.25)'
                        : 'rgba(139,92,246,0.08)',
                      opacity: dragToolsetInfo?.toolsetId === toolset.id ? 0.5 : 1,
                      border: dragOverTarget?.type === 'toolset' && dragOverTarget?.id === toolset.id
                        ? '2px solid #3b82f6'
                        : '2px solid transparent',
                      transition: 'all 0.15s ease'
                    }}>
                      {/* 插入线指示器 */}
                      {dragOverTarget?.type === 'toolset' && dragOverTarget?.id === toolset.id && (
                        <div style={{
                          position: 'absolute',
                          top: -2,
                          left: 0,
                          right: 0,
                          height: 3,
                          background: '#3b82f6',
                          borderRadius: 2,
                          zIndex: 10,
                          boxShadow: '0 0 6px rgba(59,130,246,0.5)'
                        }} />
                      )}
                      <span onClick={() => setCollapsed(c => ({ ...c, [toolset.id]: !c[toolset.id] }))} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        {isCollapsed ? <ChevronRight size={14} color="#8b5cf6" /> : <ChevronDown size={14} color="#8b5cf6" />}
                      </span>
                      <GripVertical size={14} color="#8b5cf6" style={{ flexShrink: 0, cursor: isRenaming ? 'default' : 'grab' }} />
                      {toolset.logoUrl ? (
                        <img src={toolset.logoUrl} alt={toolset.name} style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                      ) : (
                        <Package size={14} color="#8b5cf6" style={{ flexShrink: 0 }} />
                      )}
                      {isRenaming ? (
                        <>
                          <input autoFocus style={{ ...inputStyle, flex: 1, padding: '3px 8px', fontSize: 12 }} value={renameToolsetVal}
                            onChange={e => setRenameToolsetVal(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleRenameToolset(toolset.id); if (e.key === 'Escape') setRenameToolsetId(null); }}
                            onClick={e => e.stopPropagation()} />
                          <button onClick={() => handleRenameToolset(toolset.id)} title="保存"
                            style={{ width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Check size={14} color="#10b981" />
                          </button>
                          <button onClick={() => setRenameToolsetId(null)} title="取消"
                            style={{ width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <X size={14} color="#64748b" />
                          </button>
                        </>
                      ) : (
                        <>
                          <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#e2c98a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{toolset.name}</span>
                          <button onClick={() => { setEditToolsetLogo({ id: toolset.id, name: toolset.name, logoUrl: toolset.logoUrl || '' }); setEntityLogoLoadError(false); }} title="编辑工具集 Logo"
                            style={{ width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Sparkles size={12} color="var(--text-secondary)" />
                          </button>
                          <button onClick={() => { setRenameToolsetId(toolset.id); setRenameToolsetVal(toolset.name); }} title="重命名工具集"
                            style={{ width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Pencil size={12} color="var(--text-secondary)" />
                          </button>
                          <button onClick={() => { setGroupInputFor(groupInputFor === toolset.id ? null : toolset.id); setNewGroupName(''); }} title="新增分组"
                            style={{ width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FolderPlus size={14} color="var(--text-secondary)" />
                          </button>
                          <button onClick={() => setConfirm({ kind: 'toolset', id: toolset.id, name: toolset.name })} title="删除工具集"
                            style={{ width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Trash2 size={13} color="#64748b" />
                          </button>
                        </>
                      )}
                    </div>

                    {/* 新增分组输入框 */}
                    {groupInputFor === toolset.id && (
                      <div style={{ display: 'flex', gap: 6, padding: '6px 4px 6px 22px' }}>
                        <input autoFocus style={{ ...inputStyle, padding: '6px 8px', fontSize: 12 }} value={newGroupName}
                          onChange={e => setNewGroupName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleAddGroup(toolset.id); if (e.key === 'Escape') setGroupInputFor(null); }}
                          placeholder="分组名" />
                        <button onClick={() => handleAddGroup(toolset.id)} style={{ ...btn(true), padding: '6px 10px', fontSize: 12 }}>添加</button>
                      </div>
                    )}

                    {/* 分组 + 技能 */}
                    {!isCollapsed && (toolset.groups || []).map(group => {
                      const isRenamingGroup = renameGroupId === group.id;
                      return (
                      <div key={group.id} style={{ paddingLeft: 14, marginTop: 4, position: 'relative' }}
                        draggable={!isRenamingGroup}
                        onDragStart={(e) => {
                          if (!isRenamingGroup) {
                            setDragGroupInfo({ groupId: group.id, toolsetId: toolset.id });
                            e.dataTransfer.effectAllowed = 'move';
                          }
                        }}
                        onDragOver={(e) => {
                          if (dragGroupInfo && dragGroupInfo.groupId !== group.id && dragGroupInfo.toolsetId === toolset.id) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            setDragOverTarget({ type: 'group', id: group.id });
                          }
                        }}
                        onDragLeave={() => setDragOverTarget(null)}
                        onDrop={(e) => {
                          e.preventDefault();
                          handleDropOnGroup(group, toolset.id);
                        }}>
                        {/* 插入线指示器 */}
                        {dragOverTarget?.type === 'group' && dragOverTarget?.id === group.id && (
                          <div style={{
                            position: 'absolute',
                            top: -2,
                            left: 14,
                            right: 0,
                            height: 3,
                            background: '#3b82f6',
                            borderRadius: 2,
                            zIndex: 10,
                            boxShadow: '0 0 6px rgba(59,130,246,0.5)'
                          }} />
                        )}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '4px 4px',
                          borderBottom: '1px solid var(--border-subtle)',
                          background: dragGroupInfo?.groupId === group.id
                            ? 'rgba(59,130,246,0.08)'
                            : 'transparent',
                          opacity: dragGroupInfo?.groupId === group.id ? 0.5 : 1,
                          border: dragOverTarget?.type === 'group' && dragOverTarget?.id === group.id
                            ? '2px solid #3b82f6'
                            : '2px solid transparent',
                          borderBottom: '1px solid var(--border-subtle)',
                          transition: 'all 0.15s ease'
                        }}>
                          {isRenamingGroup ? (
                            <>
                              <input autoFocus style={{ ...inputStyle, flex: 1, padding: '3px 8px', fontSize: 11 }} value={renameGroupVal}
                                onChange={e => setRenameGroupVal(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleRenameGroup(group.id); if (e.key === 'Escape') setRenameGroupId(null); }}
                                onClick={e => e.stopPropagation()} />
                              <button onClick={() => handleRenameGroup(group.id)} title="保存"
                                style={{ width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Check size={13} color="#10b981" />
                              </button>
                              <button onClick={() => setRenameGroupId(null)} title="取消"
                                style={{ width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <X size={13} color="#64748b" />
                              </button>
                            </>
                          ) : (
                            <>
                              {group.logoUrl ? (
                                <img src={group.logoUrl} alt={group.name} style={{ width: 14, height: 14, objectFit: 'contain', borderRadius: 2, flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                              ) : (
                                <FolderPlus size={12} color="var(--text-secondary)" style={{ flexShrink: 0, opacity: 0.5 }} />
                              )}
                              <GripVertical size={12} color="var(--text-secondary)" style={{ flexShrink: 0, cursor: 'grab', opacity: 0.6 }} />
                              <span style={{ flex: 1, fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600, letterSpacing: '0.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.name}</span>
                              <button onClick={() => handleToggleGroupShowName(group)}
                                title={group.showName !== false ? '前台显示分组名（点击隐藏，本面板始终可见）' : '前台已隐藏分组名（点击恢复显示）'}
                                style={{ width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {group.showName !== false
                                  ? <Eye size={11} color="var(--text-secondary)" />
                                  : <EyeOff size={11} color="#f59e0b" />}
                              </button>
                              <button onClick={() => { setEditGroupLogo({ id: group.id, name: group.name, logoUrl: group.logoUrl || '', toolsetId: toolset.id }); setEntityLogoLoadError(false); }} title="编辑分组 Logo"
                                style={{ width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Sparkles size={11} color="var(--text-secondary)" />
                              </button>
                              <button onClick={() => { setRenameGroupId(group.id); setRenameGroupVal(group.name); }} title="重命名分组"
                                style={{ width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Pencil size={11} color="var(--text-secondary)" />
                              </button>
                              <button onClick={() => newPreset(toolset.id, group.id)} title="在此分组新增技能"
                                style={{ width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Plus size={13} color="var(--text-secondary)" />
                              </button>
                              <button onClick={() => setConfirm({ kind: 'group', id: group.id, name: group.name })} title="删除分组（含组内技能）"
                                style={{ width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Trash2 size={12} color="#64748b" />
                              </button>
                            </>
                          )}
                        </div>
                        {group.presets.length === 0 ? (
                          <div style={{ fontSize: 10, color: '#475569', padding: '4px 8px' }}>（空，点 + 添加技能）</div>
                        ) : group.presets.map((p, idx) => {
                          const ep = { ...p, toolsetId: toolset.id };  // 补 toolsetId 供表单回填
                          const isActive = form.id === p.id;
                          const isTop = idx === 0;
                          return (
                            <div key={p.id}
                              draggable
                              onDragStart={() => setDragInfo({ presetId: p.id, groupId: group.id })}
                              onDragOver={(e) => {
                                e.preventDefault();
                                setDragOverTarget({ type: 'preset', id: p.id });
                              }}
                              onDragLeave={() => setDragOverTarget(null)}
                              onDrop={() => handleDropOnPreset({ ...p, groupId: group.id })}
                              onClick={() => selectPreset(ep)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '6px 8px',
                                paddingRight: 52,
                                marginBottom: 3,
                                marginLeft: 8,
                                borderRadius: 6,
                                cursor: 'pointer',
                                position: 'relative',
                                background: dragInfo?.presetId === p.id
                                  ? 'rgba(59,130,246,0.15)'
                                  : isActive ? 'rgba(59,130,246,0.2)' : 'var(--modal-input-bg)',
                                opacity: dragInfo?.presetId === p.id ? 0.5 : 1,
                                border: dragOverTarget?.type === 'preset' && dragOverTarget?.id === p.id
                                  ? '2px solid #3b82f6'
                                  : `1px solid ${isActive ? '#3b82f6' : 'transparent'}`,
                                transition: 'all 0.15s'
                              }}
                              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'var(--modal-input-bg)'; }}>
                              {/* 插入线指示器 */}
                              {dragOverTarget?.type === 'preset' && dragOverTarget?.id === p.id && (
                                <div style={{
                                  position: 'absolute',
                                  top: -2,
                                  left: 0,
                                  right: 0,
                                  height: 3,
                                  background: '#3b82f6',
                                  borderRadius: 2,
                                  zIndex: 10,
                                  boxShadow: '0 0 6px rgba(59,130,246,0.5)'
                                }} />
                              )}
                              <GripVertical size={13} color="#475569" style={{ flexShrink: 0, cursor: 'grab' }} />
                              {p.logoUrl ? (
                                <img src={p.logoUrl} alt={p.name} style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 3 }} onError={e => { e.target.style.display = 'none'; }} />
                              ) : (<Sparkles size={14} color="var(--text-secondary)" style={{ flexShrink: 0 }} />)}
                              <span style={{ flex: 1, fontSize: 12, color: isActive ? 'var(--text-body)' : 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: p.enabled ? 1 : 0.5 }}>{p.name}</span>
                              {isTop && <span title="置顶技能：作为该工具集在工具条上的图标" style={{ fontSize: 8, color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.4)', borderRadius: 3, padding: '0 3px', flexShrink: 0 }}>顶</span>}
                              <button onClick={(e) => { e.stopPropagation(); handleTogglePreset(p); }}
                                style={{ position: 'absolute', right: 28, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                title={p.enabled ? '点击禁用' : '点击启用'}>
                                {p.enabled ? <CheckCircle2 size={16} color="#10b981" /> : <Circle size={16} color="#6b7280" />}
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); setConfirm({ kind: 'preset', id: p.id, name: p.name }); }}
                                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 18, height: 18, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                title="删除">
                                <Trash2 size={13} color="#f87171" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* 右：配置表单 */}
          <div className="custom-scrollbar" style={{ flex: 1, padding: 20, overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-body)' }}>{form.id ? '编辑技能' : '新增技能'}</div>
              {/* 启用用户可见：移到标题栏右侧 */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-body)' }}
                onClick={() => setForm(f => ({ ...f, enabled: !f.enabled }))}>
                {form.enabled ? <CheckCircle2 size={18} color="#10b981" /> : <Circle size={18} color="#6b7280" />}
                <span style={{ fontWeight: 500 }}>启用用户可见</span>
              </label>
            </div>

            {/* LOGO */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>技能 LOGO（节点下拉图标；组内置顶技能作为工具集图标显示在工具条）</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 48, height: 48, borderRadius: 8, background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {form.logoUrl && !logoLoadError && <img src={form.logoUrl} onError={() => setLogoLoadError(true)} onLoad={() => setLogoLoadError(false)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="Logo" />}
                  <Sparkles size={20} style={{ color: '#64748b', opacity: 0.5, display: (form.logoUrl && !logoLoadError) ? 'none' : 'flex' }} />
                </div>
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }} id="preset-logo-upload" onChange={handleLogoUpload} />
                <label htmlFor="preset-logo-upload" style={{ padding: '8px 16px', background: logoUploading ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 13, cursor: logoUploading ? 'not-allowed' : 'pointer', userSelect: 'none' }}>
                  {logoUploading ? '上传中...' : '上传 LOGO'}
                </label>
                {form.logoUrl && <button onClick={() => { setForm(f => ({ ...f, logoUrl: '' })); setLogoLoadError(false); }} style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', fontSize: 13, cursor: 'pointer' }}>清除</button>}
              </div>
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>支持 PNG、JPG、SVG、WebP，建议 48x48 或更大</div>
            </div>

            {/* 名称 */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>技能名 *</label>
              <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="如：九宫格、角色定妆" />
            </div>

            {/* 工具集 + 分组（只能选；分组随工具集联动） */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>所属工具集 *（左侧新建）</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.toolsetId == null ? '' : form.toolsetId}
                  onChange={e => {
                    const tid = e.target.value === '' ? null : Number(e.target.value);
                    // 切工具集时清空分组（强制重新选当前工具集下的分组）
                    setForm(f => ({ ...f, toolsetId: tid, groupId: null }));
                  }}>
                  <option value="">请选择工具集</option>
                  {toolsetOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>所属分组 *</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.groupId == null ? '' : form.groupId}
                  disabled={form.toolsetId == null}
                  onChange={e => setForm(f => ({ ...f, groupId: e.target.value === '' ? null : Number(e.target.value) }))}>
                  <option value="">{form.toolsetId == null ? '请先选工具集' : '请选择分组'}</option>
                  {groupOptionsForToolset(form.toolsetId).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
            </div>

            {/* 提示词 */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>附加提示词 *（生图时拼在用户输入最前，注入大模型）</label>
              <textarea style={{ ...inputStyle, minHeight: 120, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} value={form.prompt} onChange={e => setForm(f => ({ ...f, prompt: e.target.value }))}
                placeholder="如：将参考图人物生成 3x3 九宫格不同角度表情，统一画风..." />
            </div>

            {/* 描述词 */}
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>描述词（生成节点输入框灰色提示文字）</label>
              <textarea style={{ ...inputStyle, minHeight: 64, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="如：可补充表情 / 服装等细节，留空将使用技能默认效果" />
            </div>

            {/* 可用模型：勾选后，用户在画布点该技能弹出的生成节点里只能选这些模型 */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ ...labelStyle, marginBottom: 0 }}>可用图像模型（不勾选 = 不限制，显示全部）</label>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button onClick={() => setForm(f => ({ ...f, modelIds: imageModels.map(m => m.id) }))}
                    style={{ background: 'none', border: 'none', color: '#60a5fa', fontSize: 11, cursor: 'pointer', padding: 0 }}>全选</button>
                  <button onClick={() => setForm(f => ({ ...f, modelIds: [] }))}
                    style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 11, cursor: 'pointer', padding: 0 }}>清空</button>
                </div>
              </div>
              <div className="custom-scrollbar" style={{ maxHeight: 168, overflowY: 'auto', border: '1px solid var(--border-strong)', borderRadius: 8, background: 'var(--modal-input-bg)', padding: 6 }}>
                {imageModels.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', padding: '8px 6px' }}>暂无已启用的图像模型</div>
                )}
                {imageModels.map(m => {
                  const checked = (form.modelIds || []).some(x => String(x) === String(m.id));
                  return (
                    <div key={m.id}
                      onClick={() => setForm(f => {
                        const cur = f.modelIds || [];
                        return { ...f, modelIds: checked ? cur.filter(x => String(x) !== String(m.id)) : [...cur, m.id] };
                      })}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 6px', borderRadius: 6, cursor: 'pointer', background: checked ? 'rgba(59,130,246,0.12)' : 'transparent' }}>
                      {checked ? <CheckCircle2 size={16} color="#10b981" style={{ flexShrink: 0 }} /> : <Circle size={16} color="#6b7280" style={{ flexShrink: 0 }} />}
                      {m.logo_url
                        ? <img src={m.logo_url} alt="" style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'contain', flexShrink: 0 }} />
                        : <div style={{ width: 18, height: 18, flexShrink: 0 }} />}
                      <span style={{ fontSize: 12, color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
                已选 {(form.modelIds || []).length} 个{(form.modelIds || []).length === 0 ? '（节点内可选全部图像模型）' : ''}
              </div>
            </div>

            {/* 🎨 风格参考图功能 */}
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 16, marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-body)', marginBottom: 12 }}
                onClick={() => setForm(f => ({ ...f, referenceEnabled: !f.referenceEnabled }))}>
                {form.referenceEnabled ? <CheckCircle2 size={18} color="#10b981" /> : <Circle size={18} color="#6b7280" />}
                启用参考图
              </label>

              {/* 🎨 参考图标签配置 */}
              {form.referenceEnabled && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                    参考图类型名称（如：风格/场景/氛围等）
                  </label>
                  <input
                    type="text"
                    value={form.referenceLabel || '风格'}
                    onChange={e => setForm(f => ({ ...f, referenceLabel: e.target.value }))}
                    placeholder="风格"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: 13,
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 6,
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>
              )}

              {/* 🎨 参考图管理区（启用后才展开） */}
              {form.referenceEnabled && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                    {form.referenceLabel || '风格'}参考图（用户可在节点控制台选择，附加给 AI 模型）
                  </div>

                  {/* 上传按钮 */}
                  <div style={{ marginBottom: 12 }}>
                    <input type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} id="ref-image-upload" onChange={handleRefImageUpload} />
                    <label htmlFor="ref-image-upload" style={{
                      padding: '8px 16px',
                      background: refImageUploading ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.15)',
                      border: '1px solid rgba(59,130,246,0.3)',
                      borderRadius: 6,
                      color: '#60a5fa',
                      fontSize: 13,
                      cursor: refImageUploading ? 'not-allowed' : 'pointer',
                      userSelect: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <span>+</span>
                      {refImageUploading ? '上传中...' : '上传参考图'}
                    </label>
                  </div>

                  {/* 参考图网格 */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                    padding: 12,
                    background: 'var(--modal-input-bg)',
                    border: '1px solid var(--border-strong)',
                    borderRadius: 8,
                    minHeight: 100
                  }}>
                    {(!form.referenceImages || form.referenceImages.length === 0) && (
                      <div style={{ width: '100%', textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)', padding: '20px 0' }}>
                        暂无参考图，点击上方按钮上传
                      </div>
                    )}
                    {(form.referenceImages || []).map((img) => (
                      <div key={img.id} style={{
                        position: 'relative',
                        width: 120,
                        background: 'var(--modal-surface)',
                        border: '1px solid var(--border-subtle)',
                        borderRadius: 8,
                        overflow: 'hidden',
                        padding: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6
                      }}>
                        {/* 缩略图 */}
                        <div style={{
                          width: '100%',
                          height: 100,
                          borderRadius: 6,
                          overflow: 'hidden',
                          background: '#000',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <img src={img.thumbnailUrl} alt={img.name} style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain'
                          }} />
                        </div>

                        {/* 名称（可编辑） */}
                        <input
                          type="text"
                          value={img.name}
                          onChange={(e) => handleUpdateRefImageName(img.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '100%',
                            padding: '4px 6px',
                            fontSize: 11,
                            color: 'var(--text-body)',
                            background: 'transparent',
                            border: '1px solid transparent',
                            borderRadius: 4,
                            outline: 'none',
                            textAlign: 'center'
                          }}
                          onFocus={(e) => e.target.style.border = '1px solid #3b82f6'}
                          onBlur={(e) => e.target.style.border = '1px solid transparent'}
                        />

                        {/* 删除按钮 */}
                        <button
                          onClick={() => handleDeleteRefImage(img.id)}
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            width: 20,
                            height: 20,
                            padding: 0,
                            background: 'rgba(0,0,0,0.6)',
                            border: 'none',
                            borderRadius: 4,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          title="删除">
                          <X size={14} color="#f87171" />
                        </button>

                        {/* 编辑提示词按钮 */}
                        <button
                          onClick={() => setEditingRefImage({ index: img.id, name: img.name, prompt: img.prompt })}
                          style={{
                            width: '100%',
                            padding: '4px 6px',
                            fontSize: 10,
                            color: '#60a5fa',
                            background: 'rgba(59,130,246,0.1)',
                            border: '1px solid rgba(59,130,246,0.3)',
                            borderRadius: 4,
                            cursor: 'pointer'
                          }}>
                          编辑提示词
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleSave} disabled={saving} style={{ ...btn(true), opacity: saving ? 0.6 : 1 }}>{saving ? '保存中...' : (form.id ? '更新技能' : '新增技能')}</button>
              {form.id && <button onClick={() => newPreset()} style={btn(false)}>取消编辑</button>}
            </div>
          </div>
        </div>
      </div>

      {toastMessage && <AlertToast message={toastMessage} type={toastType} position="center" />}
      {confirm && (
        <ConfirmModal
          title={confirm.kind === 'toolset' ? '删除工具集' : confirm.kind === 'group' ? '删除分组' : '删除技能'}
          message={confirm.kind === 'toolset'
            ? `确定删除工具集「${confirm.name}」？其下所有分组和技能都将被删除，不可恢复。`
            : confirm.kind === 'group'
              ? `确定删除分组「${confirm.name}」？组内所有技能将一并删除，不可恢复。`
              : `确定删除技能「${confirm.name}」？此操作不可恢复。`}
          confirmText="删除"
          onConfirm={handleDelete} onCancel={() => setConfirm(null)} />
      )}

      {/* 工具集 Logo 编辑弹窗 */}
      {editToolsetLogo && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4200 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setEditToolsetLogo(null); }}>
          <div style={{ width: 420, background: 'var(--modal-surface)', border: '1px solid var(--border-strong)', borderRadius: 12, padding: 20 }}
            onMouseDown={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-body)', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>编辑工具集 Logo</span>
              <X size={18} style={{ cursor: 'pointer' }} onClick={() => setEditToolsetLogo(null)} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>工具集：{editToolsetLogo.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: 8, background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                {editToolsetLogo.logoUrl && !entityLogoLoadError && <img src={editToolsetLogo.logoUrl} onError={() => setEntityLogoLoadError(true)} onLoad={() => setEntityLogoLoadError(false)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="Logo" />}
                {(!editToolsetLogo.logoUrl || entityLogoLoadError) && <Package size={28} style={{ color: '#64748b', opacity: 0.5 }} />}
              </div>
              <div style={{ flex: 1 }}>
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }} id="toolset-logo-upload" onChange={handleEntityLogoUpload} />
                <label htmlFor="toolset-logo-upload" style={{ padding: '8px 16px', background: uploadingEntityLogo ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 13, cursor: uploadingEntityLogo ? 'not-allowed' : 'pointer', userSelect: 'none', display: 'inline-block' }}>
                  {uploadingEntityLogo ? '上传中...' : '上传 Logo'}
                </label>
                {editToolsetLogo.logoUrl && <button onClick={() => { setEditToolsetLogo(e => ({ ...e, logoUrl: '' })); setEntityLogoLoadError(false); }} style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', fontSize: 13, cursor: 'pointer', marginLeft: 8 }}>清除</button>}
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>建议 48x48 或更大，PNG/JPG/SVG</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditToolsetLogo(null)} style={btn(false)}>取消</button>
              <button onClick={handleSaveToolsetLogo} style={btn(true)}>保存</button>
            </div>
          </div>
        </div>
      )}

      {/* 分组 Logo 编辑弹窗 */}
      {editGroupLogo && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4200 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setEditGroupLogo(null); }}>
          <div style={{ width: 420, background: 'var(--modal-surface)', border: '1px solid var(--border-strong)', borderRadius: 12, padding: 20 }}
            onMouseDown={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-body)', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>编辑分组 Logo</span>
              <X size={18} style={{ cursor: 'pointer' }} onClick={() => setEditGroupLogo(null)} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>分组：{editGroupLogo.name}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 64, height: 64, borderRadius: 8, background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                {editGroupLogo.logoUrl && !entityLogoLoadError && <img src={editGroupLogo.logoUrl} onError={() => setEntityLogoLoadError(true)} onLoad={() => setEntityLogoLoadError(false)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="Logo" />}
                {(!editGroupLogo.logoUrl || entityLogoLoadError) && <FolderPlus size={28} style={{ color: '#64748b', opacity: 0.5 }} />}
              </div>
              <div style={{ flex: 1 }}>
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }} id="group-logo-upload" onChange={handleEntityLogoUpload} />
                <label htmlFor="group-logo-upload" style={{ padding: '8px 16px', background: uploadingEntityLogo ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 13, cursor: uploadingEntityLogo ? 'not-allowed' : 'pointer', userSelect: 'none', display: 'inline-block' }}>
                  {uploadingEntityLogo ? '上传中...' : '上传 Logo'}
                </label>
                {editGroupLogo.logoUrl && <button onClick={() => { setEditGroupLogo(e => ({ ...e, logoUrl: '' })); setEntityLogoLoadError(false); }} style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', fontSize: 13, cursor: 'pointer', marginLeft: 8 }}>清除</button>}
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>建议 48x48 或更大，PNG/JPG/SVG</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditGroupLogo(null)} style={btn(false)}>取消</button>
              <button onClick={handleSaveGroupLogo} style={btn(true)}>保存</button>
            </div>
          </div>
        </div>
      )}

      {/* 🎨 参考图提示词编辑弹窗 */}
      {editingRefImage && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4200 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setEditingRefImage(null); }}>
          <div style={{ width: 480, background: 'var(--modal-surface)', border: '1px solid var(--border-strong)', borderRadius: 12, padding: 20 }}
            onMouseDown={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-body)', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>编辑参考图提示词</span>
              <X size={18} style={{ cursor: 'pointer' }} onClick={() => setEditingRefImage(null)} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>参考图：{editingRefImage.name}</div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>提示词模板</label>
              <textarea
                value={editingRefImage.prompt}
                onChange={(e) => setEditingRefImage(prev => ({ ...prev, prompt: e.target.value }))}
                rows={4}
                style={{
                  ...inputStyle,
                  resize: 'vertical',
                  minHeight: 80
                }}
                placeholder="例如：以下图为参考，模仿其风格生成..."
              />
              <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>
                此文本会在生成时附加到提示词中
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditingRefImage(null)} style={btn(false)}>取消</button>
              <button onClick={() => {
                handleUpdateRefImagePrompt(editingRefImage.index, editingRefImage.prompt);
                setEditingRefImage(null);
              }} style={btn(true)}>保存</button>
            </div>
          </div>
        </div>
      )}

      {/* 🎨 图片裁剪弹窗 */}
      {cropImageUrl && (
        <SimpleImageCropModal
          imageUrl={cropImageUrl}
          onSave={handleCropComplete}
          onCancel={() => setCropImageUrl(null)}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}


